import { debounce } from '@mui/material/utils';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { matchPath, useLocation, useNavigate } from 'react-router';

import { QuickSearch } from '@/components/QuickSearch/QuickSearch';
import { paths } from '@/core/router/paths';

import { useQueryParams } from '../../../utils/url/useQueryParams';

import { headerSearchBarStyles as sx } from './HeaderSearchBar.styles';

const DEBOUNCE_THRESHOLD = 300;

export const HeaderSearchBar = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { pathname } = useLocation();

	const [searchValue, setSearchValue] = useState('');
	const { name, setQueryParams } = useQueryParams();

	const updateUrlParams = useCallback(
		(n: string) => {
			setQueryParams({ name: n, page: undefined });
		},
		[setQueryParams]
	);

	const updateUrlParamsDebounced = useMemo(() => {
		return debounce((n: string) => {
			updateUrlParams(n);
		}, DEBOUNCE_THRESHOLD);
	}, [updateUrlParams]);

	const handleChange = (value: string) => {
		setSearchValue(value);
	};

	const handleClear = () => {
		setSearchValue(() => {
			setQueryParams({ name: '', page: undefined });
			return '';
		});
	};

	const handleSearch = (value: string) => {
		if (
			!pathname.includes(paths.searchHcp) &&
			!pathname.includes(paths.searchHco)
		) {
			if (matchPath(paths.hco, pathname)) {
				navigate(`${paths.searchHco}?name=${encodeURIComponent(value)}`, {
					replace: true
				});
			} else {
				navigate(`${paths.searchHcp}?name=${encodeURIComponent(value)}`, {
					replace: true
				});
			}
		} else {
			// Already on the correct page, update the query params
			updateUrlParamsDebounced(decodeURIComponent(value));
		}
	};

	useEffect(() => {
		setSearchValue(decodeURIComponent(name));
	}, [name]);

	return (
		<QuickSearch
			value={searchValue}
			onChange={handleChange}
			placeholder={t('Search OpenData')}
			handleClear={handleClear}
			handleSearch={handleSearch}
			customSx={{
				root: sx.root,
				searchRoot: sx.searchRoot,
				searchInput: sx.inputField,
				searchIcon: sx.searchIcon,
				dropdown: sx.dropdown
			}}
		/>
	);
};
